import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@utils/icons';
import type { IAreaEditor } from './matrixCellPropsEqual';
import type { IProgressMatrix } from './progressMatrix';
import { ColumnHeader, HEADER_ROW, LABEL_COL } from './MatrixColumnHeader';
import { FloorCells, FloorLabel, ZoneCells, ZoneLabel } from './MatrixRows';
import { useVisibleGrid } from './useVisibleGrid';
import { COL_W } from './visibleCols';
import { HEADER_ROW_H, buildRowGeometry } from './visibleRows';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './matrixTable.module.css';

export interface IMatrixTableProps {
  matrix: IProgressMatrix;
  /** Open an activity's drawer (clicking its column header). */
  onOpenActivity?: (taskId: string) => void;
  /** Deep-link an activity into the Scheduler (settings → Show in scheduler). */
  onShowInScheduler?: (taskId: string) => void;
  /** A dependency jump whose target isn't a column at the CURRENT generation —
   *  ProgressTracker switches to the target's generation, or offers the
   *  Scheduler when the task has no locations at all. */
  onJumpMissing?: (taskId: string, taskName: string) => void;
  /** Jump to run once its column exists (set after a generation switch). */
  pendingJumpTaskId?: string | null;
  onPendingJumpDone?: () => void;
  /** Change an area's status by clicking its cell (task columns only). */
  areaEditor?: IAreaEditor;
}

/**
 * Four-pane frozen grid (Excel-style): corner + header strip on top, label
 * column + body below. Only the BODY pane scrolls — it owns both scrollbars,
 * which therefore start at the data region (after the label column, below the
 * header — the point of the layout). The header/label panes are
 * overflow:hidden viewports whose scroll positions are mirrored imperatively
 * from the body scroller (see the sync effect), replacing the previous
 * single-scroller position:sticky design.
 */
export function MatrixTable({
  matrix, onOpenActivity, onShowInScheduler, areaEditor, onJumpMissing, pendingJumpTaskId, onPendingJumpDone,
}: IMatrixTableProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggle = useCallback(
    (floorId: string) => setCollapsed((c) => ({ ...c, [floorId]: !c[floorId] })),
    [],
  );

  // Collapse / expand every floor at once. "All collapsed" flips to expand.
  const floorIds = matrix.floors.map((f) => f.floorId);
  const allCollapsed = floorIds.length > 0 && floorIds.every((id) => collapsed[id]);
  const toggleAll = () =>
    setCollapsed(allCollapsed ? {} : Object.fromEntries(floorIds.map((id) => [id, true])));

  // Crosshair hover: light the whole row + column of the hovered cell. Done by
  // direct class toggling (via delegation) rather than React state so the matrix
  // never re-renders on mouse move. Hangs on the FRAME so one delegation covers
  // all four panes, and the queries span panes (a cell's header lives in the
  // header pane, its label in the label pane).
  const gridRef = useRef<HTMLDivElement>(null);
  // State, not a ref: the scroller unmounts (see the empty-columns early return
  // below) and remounts a new element once columns come back. useVisibleGrid
  // keys its subscription effect on this value, so it re-subscribes on remount
  // — a ref wouldn't (see useVisibleGrid's doc comment for the full story).
  const [scrollerEl, setScrollerEl] = useState<HTMLDivElement | null>(null);
  // Plain refs are enough for the mirrored panes: they mount/unmount together
  // with the scroller (same early return), and the sync effect below is keyed
  // on scrollerEl, so a remount re-runs it and re-reads these refs' fresh
  // elements.
  const headerViewportRef = useRef<HTMLDivElement>(null);
  const labelViewportRef = useRef<HTMLDivElement>(null);

  // Row geometry: one floor row per floor, its zone rows following only when
  // it isn't collapsed. Recomputed only when the floor list or the collapse
  // map changes — not on every render.
  const rowGeometry = useMemo(() => buildRowGeometry(matrix.floors, collapsed), [matrix.floors, collapsed]);

  // Two-axis windowing, one subscription: only ~a-viewport-plus-buffer of the
  // (potentially hundreds of) 140px columns AND body rows is mounted. All grid
  // TRACKS stay in the pane templates on both axes, so the body pane's
  // scrollbars and total scroll dimensions are identical to a full render.
  // `null` (pre-layout / jsdom) = render everything, on both axes.
  const win = useVisibleGrid(scrollerEl, matrix.columns.length, rowGeometry);
  const colRange = win?.cols;
  const colStart = colRange?.start ?? 0;
  const visibleColumns = useMemo(
    () => (colRange ? matrix.columns.slice(colRange.start, colRange.end + 1) : matrix.columns),
    [matrix.columns, colRange],
  );
  const rowRange = win?.rows;
  const rowStart = rowRange?.start ?? 0;
  // A row that stays visible across a window shift (scrolling within the
  // buffer) keeps its absolute index into rowGeometry.rows, hence the same
  // gridRow — same stability invariant visibleColumns relies on for columns,
  // which is what lets the per-row/per-cell memos bail on an unrelated scroll.
  const visibleRows = useMemo(
    () => (rowRange ? rowGeometry.rows.slice(rowRange.start, rowRange.end + 1) : rowGeometry.rows),
    [rowGeometry.rows, rowRange],
  );

  // Per-pane templates. Fixed COL_W columns (not 1fr): the pane grids size to
  // their tracks (width: max-content), so the body pane's scrollWidth is
  // exactly N×COL_W and the header pane mirrors it 1:1. All tracks stay in
  // the templates even when the rendered ITEMS are windowed — see
  // visibleColumns/visibleRows above. Row tracks come from rowGeometry, which
  // must agree with the CSS constants documented in visibleRows.ts
  // (HEADER_ROW_H / FLOOR_ROW_H / ZONE_ROW_H) — keep the two in sync.
  const headerGridStyle = useMemo(
    () => ({
      gridTemplateColumns: `repeat(${matrix.columns.length}, ${COL_W}px)`,
      gridTemplateRows: `${HEADER_ROW_H}px`,
    }),
    [matrix.columns.length],
  );
  const labelGridStyle = useMemo(
    () => ({
      gridTemplateColumns: 'var(--tracker-label-w)',
      gridTemplateRows: rowGeometry.templateRows,
    }),
    [rowGeometry],
  );
  const bodyGridStyle = useMemo(
    () => ({
      gridTemplateColumns: `repeat(${matrix.columns.length}, ${COL_W}px)`,
      gridTemplateRows: rowGeometry.templateRows,
    }),
    [matrix.columns.length, rowGeometry],
  );

  // Mirror the body pane's scroll onto the frozen panes SYNCHRONOUSLY in the
  // scroll handler — an rAF deferral here would lag the header/labels one
  // frame behind the body during fast scroll (the desync users notice
  // immediately). Distinct from useVisibleGrid's subscription, which IS
  // rAF-coalesced: re-windowing can lag a frame, pane alignment cannot.
  // Wheel input over the frozen panes is forwarded to the scroller (passive,
  // no preventDefault) so the wheel works everywhere on the table.
  useEffect(() => {
    const el = scrollerEl;
    if (!el) return;
    const sync = () => {
      if (headerViewportRef.current) headerViewportRef.current.scrollLeft = el.scrollLeft;
      if (labelViewportRef.current) labelViewportRef.current.scrollTop = el.scrollTop;
    };
    const forwardWheel = (e: WheelEvent) => el.scrollBy({ left: e.deltaX, top: e.deltaY });
    sync();
    el.addEventListener('scroll', sync, { passive: true });
    const header = headerViewportRef.current;
    const label = labelViewportRef.current;
    header?.addEventListener('wheel', forwardWheel, { passive: true });
    label?.addEventListener('wheel', forwardWheel, { passive: true });
    return () => {
      el.removeEventListener('scroll', sync);
      header?.removeEventListener('wheel', forwardWheel);
      label?.removeEventListener('wheel', forwardWheel);
    };
  }, [scrollerEl]);

  const markedRef = useRef<Element[]>([]);

  const crosshair = useCallback((row?: string, col?: string) => {
    const grid = gridRef.current;
    if (!grid) return;
    const mark = styles.mark as string;
    const hit = styles.hit as string;
    // Clear ONLY what the previous hover marked — a whole-grid
    // querySelectorAll per mouseover is what made hover cost scale with grid size.
    for (const el of markedRef.current) el.classList.remove(mark, hit);
    markedRef.current = [];
    if (!row || !col) return;
    const add = (el: Element | null, cls: string) => {
      if (!el) return;
      el.classList.add(cls);
      markedRef.current.push(el);
    };
    if (row === HEADER_ROW && col !== LABEL_COL) {
      grid.querySelectorAll(`[data-col="${col}"]`).forEach((el) => add(el, mark));
    } else if (col === LABEL_COL && row !== HEADER_ROW) {
      grid.querySelectorAll(`[data-row="${row}"]`).forEach((el) => add(el, mark));
    } else if (row !== HEADER_ROW && col !== LABEL_COL) {
      add(grid.querySelector(`[data-row="${HEADER_ROW}"][data-col="${col}"]`), mark);
      add(grid.querySelector(`[data-row="${row}"][data-col="${LABEL_COL}"]`), mark);
    }
    const self = grid.querySelector(`[data-row="${row}"][data-col="${col}"]`);
    if (row !== HEADER_ROW && col !== LABEL_COL) add(self, hit);
    else add(self, mark);
  }, []);

  const onMouseOver = useCallback(
    (e: MouseEvent) => {
      const cell = (e.target as HTMLElement).closest<HTMLElement>('[data-row]');
      crosshair(cell?.dataset.row, cell?.dataset.col);
    },
    [crosshair],
  );
  const onMouseLeave = useCallback(() => crosshair(), [crosshair]);

  // Dependency jump (the preview card's clickable rows): smooth-scroll the
  // target column to the viewport's center, then FLASH it — a pair of overlay
  // divs (header pane + body pane) pinned to the target's grid tracks, so the
  // pulse never touches the windowed cells or their memoization. The nonce
  // remounts the overlays when the same column is jumped to twice in a row
  // (same key would keep the finished animation's final frame). A dependency
  // that isn't a column right now (filtered out / different level / unlocated)
  // is a no-op — nothing to scroll to.
  const [jumpFlash, setJumpFlash] = useState<{ taskId: string; nonce: number } | null>(null);
  const clearJumpFlash = useCallback(() => setJumpFlash(null), []);
  const jumpToTask = useCallback(
    (taskId: string, taskName = '') => {
      const index = matrix.columns.findIndex((c) => c.taskId === taskId);
      if (index < 0) {
        // Not a column at this generation — hand the jump up to ProgressTracker,
        // which switches to the dependency's own generation when it has one.
        onJumpMissing?.(taskId, taskName);
        return;
      }
      if (!scrollerEl) return;
      const left = index * COL_W + COL_W / 2 - scrollerEl.clientWidth / 2;
      scrollerEl.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      setJumpFlash((prev) => ({ taskId, nonce: (prev?.nonce ?? 0) + 1 }));
    },
    [matrix.columns, scrollerEl, onJumpMissing],
  );

  // A jump handed back down after a generation switch: the target column only
  // exists once the relayout transition lands this matrix, so run it from an
  // effect keyed on the columns. The parent clears `pendingJumpTaskId` when the
  // task never becomes a column (filtered out at the target generation).
  useEffect(() => {
    if (!pendingJumpTaskId) return;
    if (!matrix.columns.some((c) => c.taskId === pendingJumpTaskId)) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- runs once per cross-generation jump, exactly when the target column materializes; the parent clears pendingJumpTaskId in the same commit via onPendingJumpDone
    jumpToTask(pendingJumpTaskId);
    onPendingJumpDone?.();
  }, [pendingJumpTaskId, matrix.columns, jumpToTask, onPendingJumpDone]);
  const flashIndex = jumpFlash ? matrix.columns.findIndex((c) => c.taskId === jumpFlash.taskId) : -1;

  if (matrix.columns.length === 0) {
    return <div className={styles.empty}>{t('activities.tracker.emptyGeneration')}</div>;
  }

  return (
    <div ref={gridRef} className={styles.frame} onMouseOver={onMouseOver} onMouseLeave={onMouseLeave}>
      <div className={styles.topRow}>
        {/* The whole LOCATION BREAKDOWN cell toggles collapse/expand all floors. */}
        <div
          className={`${styles.corner} ${styles.cornerToggle}`}
          data-row={HEADER_ROW}
          data-col={LABEL_COL}
          role="button"
          tabIndex={0}
          onClick={toggleAll}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleAll();
            }
          }}
          aria-label={t(allCollapsed ? 'activities.tracker.expandAll' : 'activities.tracker.collapseAll')}
          title={t(allCollapsed ? 'activities.tracker.expandAll' : 'activities.tracker.collapseAll')}
        >
          {t('activities.tracker.locationBreakdown')}
          <span className={styles.collapseAllCaret} data-collapsed={allCollapsed}>
            <ChevronDownIcon size={16} />
          </span>
        </div>
        <div className={styles.headerViewport} ref={headerViewportRef}>
          <div className={styles.headerGrid} style={headerGridStyle}>
            {visibleColumns.map((col, i) => (
              <ColumnHeader
                key={col.taskId}
                col={col}
                colIndex={colStart + i}
                t={t}
                onOpen={onOpenActivity}
                onShowInScheduler={onShowInScheduler}
                onJumpToTask={jumpToTask}
              />
            ))}
            {flashIndex >= 0 && (
              // The header half of the jump flash; its body twin below ends the
              // pair (one onAnimationEnd clears both).
              <div
                key={`flash-h-${jumpFlash!.nonce}`}
                className={styles.jumpFlash}
                style={{ gridColumn: flashIndex + 1, gridRow: 1 }}
                onAnimationEnd={clearJumpFlash}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles.bottomRow}>
        <div className={styles.labelViewport} ref={labelViewportRef}>
          <div className={styles.labelGrid} style={labelGridStyle}>
            {visibleRows.map((row, i) => {
              // Absolute grid row INTO THE FULL rowGeometry.rows (not the
              // visible slice) + 1 — the panes have no header track.
              const gridRow = rowStart + i + 1;
              const floor = matrix.floors[row.floorIndex]!;
              if (row.kind === 'floor') {
                return (
                  <FloorLabel
                    key={row.key}
                    floorId={floor.floorId}
                    floorName={floor.floorName}
                    collapsed={!!collapsed[floor.floorId]}
                    onToggle={toggle}
                    gridRow={gridRow}
                  />
                );
              }
              const zone = floor.zones[row.zoneIndex!]!;
              return (
                <ZoneLabel
                  key={row.key}
                  rowKey={row.key}
                  zoneName={zone.zoneName}
                  gridRow={gridRow}
                />
              );
            })}
          </div>
        </div>
        <ScrollArea orientation="both" className={styles.scroller} viewportRef={setScrollerEl}>
          <div className={styles.bodyGrid} style={bodyGridStyle}>
            {visibleRows.map((row, i) => {
              const gridRow = rowStart + i + 1;
              const floor = matrix.floors[row.floorIndex]!;
              if (row.kind === 'floor') {
                return (
                  <FloorCells
                    key={row.key}
                    floor={floor}
                    columns={visibleColumns}
                    colStart={colStart}
                    gridRow={gridRow}
                  />
                );
              }
              const zone = floor.zones[row.zoneIndex!]!;
              return (
                <ZoneCells
                  key={row.key}
                  zone={zone}
                  columns={visibleColumns}
                  colStart={colStart}
                  floorId={floor.floorId}
                  areaEditor={areaEditor}
                  gridRow={gridRow}
                />
              );
            })}
            {flashIndex >= 0 && (
              <div
                key={`flash-b-${jumpFlash!.nonce}`}
                className={styles.jumpFlash}
                style={{ gridColumn: flashIndex + 1, gridRow: '1 / -1' }}
              />
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
